import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import algoliasearch from 'algoliasearch/lite';

import { useTenantContext } from '~/context/TenantProvider';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import { GET_ALGOLIA_API_KEY } from '~/graphql/user';
import { isNewAlgoliaIndexesPresent } from '~/utils/env';

const algoliaName = process.env.REACT_APP_ALGOLIA_APP_NAME || '';

// todo: use process.env variables directly instead of the functions after index migration
function getAlgoliaIndexName(tenantName?: string | null) {
  if (isNewAlgoliaIndexesPresent()) {
    return process.env.REACT_APP_ALGOLIA_CONTENT_AGGREGATOR_INDEX_NAME;
  }

  return process.env.REACT_APP_ALGOLIA_INDEX_NAME?.replace('tenantname', tenantName || '');
}

function getAlgoliaExpertsIndexName(tenantName?: string | null) {
  if (isNewAlgoliaIndexesPresent()) {
    return process.env.REACT_APP_ALGOLIA_EXPERTS_AGGREGATOR_INDEX_NAME;
  }

  return process.env.REACT_APP_ALGOLIA_EXPERTS_INDEX_NAME?.replace('tenantname', tenantName || '');
}

/**
 * Creates an algolia's search client
 * @param indexEnding (optional) Adds an ending to index name. Example: staging_content_aggregator_<indexEnding>
 */
const useSearchClient = (indexEnding?: string) => {
  const { tenant } = useTenantContext();
  const { userInfo, userLoading } = useUserInfoContext();

  const { data: algoliaData, loading: loadingAlgoliaData } = useQuery(GET_ALGOLIA_API_KEY, {
    skip: !userInfo,
  });

  const indexName = [getAlgoliaIndexName(tenant?.name), indexEnding].filter(Boolean).join('_');
  const expertsIndexName = getAlgoliaExpertsIndexName(tenant?.name) ?? '';

  const algoliaApiKey =
    userInfo && algoliaData?.getAlgoliaApiKey?.api_key
      ? algoliaData?.getAlgoliaApiKey?.api_key
      : process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

  const loadingApiKey = userLoading || loadingAlgoliaData;

  const searchClient = useMemo(() => algoliasearch(algoliaName, algoliaApiKey), [algoliaApiKey]);

  return {
    searchClient,
    indexName,
    expertsIndexName,
    loadingApiKey,
  };
};

export default useSearchClient;
